if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();

        this.modalContent = this.querySelector('.product-media-modal__content');
        this.previousButton = this.querySelector('[data-product-modal-previous]');
        this.nextButton = this.querySelector('[data-product-modal-next]');
        this.currentCounter = this.querySelector('[data-product-modal-current]');
        this.totalCounter = this.querySelector('[data-product-modal-total]');
        this.desktopZoomMedia = window.matchMedia('(min-width: 990px)');
        this.zoomScale = 1;
        this.zoomX = 0;
        this.zoomY = 0;
        this.dragPointerId = null;
        this.dragStartX = 0;
        this.dragStartY = 0;
        this.dragStartZoomX = 0;
        this.dragStartZoomY = 0;
        this.didDrag = false;

        this.previousButton?.addEventListener('click', () => this.changeMedia(-1));
        this.nextButton?.addEventListener('click', () => this.changeMedia(1));

        this.modalContent?.addEventListener('click', (event) => this.onZoomClick(event));
        this.modalContent?.addEventListener('wheel', (event) => this.onZoomWheel(event), { passive: false });
        this.modalContent?.addEventListener('pointerdown', (event) => this.onZoomPointerDown(event));
        this.modalContent?.addEventListener('pointermove', (event) => this.onZoomPointerMove(event));
        this.modalContent?.addEventListener('pointerup', (event) => this.endZoomDrag(event));
        this.modalContent?.addEventListener('pointercancel', (event) => this.endZoomDrag(event));
        this.modalContent?.addEventListener('dragstart', (event) => {
          if (this.desktopZoomMedia.matches && event.target === this.getActiveZoomImage()) event.preventDefault();
        });

        this.desktopZoomMedia.addEventListener?.('change', () => {
          this.resetZoom();
          if (this.hasAttribute('open')) this.configureZoomTarget(this.getActiveZoomImage());
        });

        this.querySelector('.product-media-modal__dialog')?.addEventListener('pointerup', (event) => {
          event.stopPropagation();
        });

        this.addEventListener('keydown', (event) => {
          if (!this.hasAttribute('open')) return;

          if (event.code === 'ArrowLeft') {
            event.preventDefault();
            this.changeMedia(-1);
          }

          if (event.code === 'ArrowRight') {
            event.preventDefault();
            this.changeMedia(1);
          }

          if (!this.desktopZoomMedia.matches) return;
          if (!this.getActiveZoomImage()) return;

          if (event.key === '+' || event.key === '=') {
            event.preventDefault();
            this.setZoom(this.zoomScale + 0.25, this.zoomX, this.zoomY);
          }

          if (event.key === '-' || event.key === '_') {
            event.preventDefault();
            this.setZoom(this.zoomScale - 0.25, this.zoomX, this.zoomY);
          }

          if (event.key === '0') {
            event.preventDefault();
            this.setZoom(1, 0, 0);
          }
        });
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const activeMediaId = this.openedBy.getAttribute('data-media-id');
        const allMedia = Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));

        this.mediaItems = allMedia.filter(
          (media, index) =>
            !media.classList.contains('product__media-item--variant') ||
            index === 0 ||
            media.getAttribute('data-media-id') === activeMediaId
        );

        const activeIndex = this.mediaItems.findIndex(
          (media) => media.getAttribute('data-media-id') === activeMediaId
        );

        this.setActiveMedia(activeIndex < 0 ? 0 : activeIndex);
      }

      changeMedia(direction) {
        if (!this.mediaItems?.length) return;
        this.setActiveMedia(this.activeMediaIndex + direction);
      }

      setActiveMedia(index) {
        if (!this.mediaItems?.length) return;

        this.resetZoom();
        this.activeMediaIndex = (index + this.mediaItems.length) % this.mediaItems.length;
        window.pauseAllMedia();

        this.querySelectorAll('.product-media-modal__content > [data-media-id]').forEach((media) => {
          media.classList.remove('active');
          media.setAttribute('aria-hidden', 'true');
        });

        const activeMedia = this.mediaItems[this.activeMediaIndex];
        activeMedia.classList.add('active');
        activeMedia.setAttribute('aria-hidden', 'false');
        this.configureZoomTarget(activeMedia);

        if (this.currentCounter) this.currentCounter.textContent = this.activeMediaIndex + 1;
        if (this.totalCounter) this.totalCounter.textContent = this.mediaItems.length;

        const hideNavigation = this.mediaItems.length <= 1;
        if (this.previousButton) this.previousButton.hidden = hideNavigation;
        if (this.nextButton) this.nextButton.hidden = hideNavigation;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      getActiveZoomImage() {
        const activeMedia = this.mediaItems?.[this.activeMediaIndex];
        return activeMedia?.nodeName === 'IMG' ? activeMedia : null;
      }

      configureZoomTarget(media) {
        if (!this.desktopZoomMedia.matches || media?.nodeName !== 'IMG') return;
        media.classList.add('product-media-modal__zoomable');
        this.applyZoom(media);
      }

      onZoomClick(event) {
        const activeImage = this.getActiveZoomImage();
        if (!this.desktopZoomMedia.matches || event.target !== activeImage) return;

        event.preventDefault();
        event.stopPropagation();

        if (this.didDrag) {
          this.didDrag = false;
          return;
        }

        this.setZoom(this.zoomScale > 1 ? 1 : 2, 0, 0);
      }

      onZoomWheel(event) {
        const activeImage = this.getActiveZoomImage();
        if (!this.desktopZoomMedia.matches || event.target !== activeImage || event.ctrlKey) return;

        event.preventDefault();

        const deltaMultiplier = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
        const normalizedDelta = Math.max(-100, Math.min(100, event.deltaY * deltaMultiplier));
        const nextScale = Math.max(1, Math.min(2, this.zoomScale - normalizedDelta * 0.0025));
        const currentRect = activeImage.getBoundingClientRect();
        const baseCenterX = currentRect.left + currentRect.width / 2 - this.zoomX;
        const baseCenterY = currentRect.top + currentRect.height / 2 - this.zoomY;
        const pointerX = event.clientX - baseCenterX;
        const pointerY = event.clientY - baseCenterY;
        const zoomRatio = nextScale / this.zoomScale;
        const nextX = pointerX - (pointerX - this.zoomX) * zoomRatio;
        const nextY = pointerY - (pointerY - this.zoomY) * zoomRatio;

        this.setZoom(nextScale, nextX, nextY);
      }

      onZoomPointerDown(event) {
        const activeImage = this.getActiveZoomImage();
        if (
          !this.desktopZoomMedia.matches ||
          event.target !== activeImage ||
          event.pointerType === 'touch' ||
          event.button !== 0 ||
          this.zoomScale <= 1
        )
          return;

        event.preventDefault();
        this.dragPointerId = event.pointerId;
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
        this.dragStartZoomX = this.zoomX;
        this.dragStartZoomY = this.zoomY;
        this.didDrag = false;
        activeImage.classList.add('is-dragging');
        activeImage.setPointerCapture?.(event.pointerId);
      }

      onZoomPointerMove(event) {
        if (event.pointerId !== this.dragPointerId) return;

        const deltaX = event.clientX - this.dragStartX;
        const deltaY = event.clientY - this.dragStartY;
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) this.didDrag = true;

        event.preventDefault();
        this.setZoom(this.zoomScale, this.dragStartZoomX + deltaX, this.dragStartZoomY + deltaY);
      }

      endZoomDrag(event) {
        if (event?.pointerId !== undefined && event.pointerId !== this.dragPointerId) return;

        const activeImage = this.getActiveZoomImage();
        if (this.dragPointerId !== null && activeImage?.hasPointerCapture?.(this.dragPointerId)) {
          activeImage.releasePointerCapture(this.dragPointerId);
        }
        activeImage?.classList.remove('is-dragging');
        this.dragPointerId = null;
      }

      setZoom(scale, x = this.zoomX, y = this.zoomY) {
        const activeImage = this.getActiveZoomImage();
        if (!this.desktopZoomMedia.matches || !activeImage) return;

        const nextScale = Math.max(1, Math.min(2, scale));
        const nextPosition = nextScale === 1 ? { x: 0, y: 0 } : this.clampZoomPosition(activeImage, nextScale, x, y);

        this.zoomScale = nextScale;
        this.zoomX = nextPosition.x;
        this.zoomY = nextPosition.y;
        this.applyZoom(activeImage);
      }

      clampZoomPosition(activeImage, scale, x, y) {
        const contentStyles = window.getComputedStyle(this.modalContent);
        const availableWidth =
          this.modalContent.clientWidth -
          parseFloat(contentStyles.paddingLeft || 0) -
          parseFloat(contentStyles.paddingRight || 0);
        const availableHeight =
          this.modalContent.clientHeight -
          parseFloat(contentStyles.paddingTop || 0) -
          parseFloat(contentStyles.paddingBottom || 0);
        const maxX = Math.max(0, (activeImage.offsetWidth * scale - availableWidth) / 2);
        const maxY = Math.max(0, (activeImage.offsetHeight * scale - availableHeight) / 2);

        return {
          x: Math.max(-maxX, Math.min(maxX, x)),
          y: Math.max(-maxY, Math.min(maxY, y)),
        };
      }

      applyZoom(activeImage) {
        activeImage.style.setProperty('--product-modal-zoom-scale', this.zoomScale);
        activeImage.style.setProperty('--product-modal-zoom-x', `${this.zoomX}px`);
        activeImage.style.setProperty('--product-modal-zoom-y', `${this.zoomY}px`);
        activeImage.classList.toggle('is-zoomed', this.zoomScale > 1);
      }

      resetZoom() {
        this.endZoomDrag();
        this.zoomScale = 1;
        this.zoomX = 0;
        this.zoomY = 0;
        this.didDrag = false;

        this.modalContent?.querySelectorAll('.product-media-modal__zoomable').forEach((media) => {
          media.classList.remove('product-media-modal__zoomable', 'is-zoomed', 'is-dragging');
          media.style.removeProperty('--product-modal-zoom-scale');
          media.style.removeProperty('--product-modal-zoom-x');
          media.style.removeProperty('--product-modal-zoom-y');
        });
      }
    }
  );
}
